import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ScheduleCard from '/@/components/bayer/schedule-card';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { reverse } from '/@/utils/router';

function SuggestionsSection({
    setOpenYieldEstimationModal,
}: {
    setOpenYieldEstimationModal: (value: boolean) => void;
}) {
    const navigate = useNavigate();
    const goToSeedRecommendation = () => {
        navigate(
            reverse('recommendations.RecommendationFieldSelect', {
                qs: { entrySource: 'rv-home-suggestions' },
                params: {
                    crop: 'corn',
                },
            })
        );
    };
    const goToCPRecommendation = ({ qs }: { qs?: any }) => {
        mpTrackEvent('Crop Protection Recommendation began', {
            entry_source: 'rv-home-suggestions',
        });
        navigate(reverse('cropProtectionRecommendation', { qs: qs }));
    };
    const goToDensityRecommendation = () => {
        mpTrackEvent('Corn Density Recommendation began', {
            entry_source: 'rv-home-suggestions',
        });
        navigate(
            reverse('densityRecommendation.DensityRecommendationCreate', {
                qs: { entrySource: 'rv-home-suggestions' },
            })
        );
    };
    const { i18n } = useTranslation();
    const suggestions = useMemo(
        () => [
            {
                cards: [
                    {
                        label: _('Siembra Temprana'),
                        title: _('Ayudá a los productores a estimar el rendimiento de su cosecha'),
                        description: _(
                            'A través de la carga muestras, los productores pueden estimar el rendimiento y conocer la fecha probable de cosecha.'
                        ),
                        actionText: _('Ver cómo calcular el rendimiento'),
                        action: () => {
                            setOpenYieldEstimationModal(true);
                        },
                    },
                    {
                        label: _('Siembra tardía'),
                        title: _('Acompañá el monitoreo del cultivo para detectar plagas y malezas'),
                        description: _(
                            'Ayudá a los productores a detectar plagas y enfermedades a tiempo para proteger el rendimiento del cultivo.'
                        ),
                        actionText: _('Recomendar herbicidas'),
                        action: () => {
                            goToCPRecommendation({ qs: { type: 'weeds' } });
                        },
                    },
                ],
                fromMonth: 0, // Enero
                toMonth: 0, // Enero
            },
            {
                cards: [
                    {
                        label: _('Siembra Temprana'),
                        title: _('Ayudá a los productores a estimar el rendimiento de su cosecha'),
                        description: _(
                            'A través de la carga muestras, los productores pueden estimar el rendimiento y conocer la fecha probable de cosecha.'
                        ),
                        actionText: _('Ver cómo calcular el rendimiento'),
                        action: () => {
                            setOpenYieldEstimationModal(true);
                        },
                    },
                    {
                        label: _('Siembra tardía'),
                        title: _('Seguí de cerca el monitoreo de plagas y enfermedades de tus productores'),
                        description: _(
                            'Evaluá el estado del cultivo y recomendales los fungicidas e insecticidas adecuados.'
                        ),
                        actionText: _('Recomendar fitosanitarios'),
                        action: () => {
                            goToCPRecommendation({});
                        },
                    },
                ],
                fromMonth: 1, // Febrero
                toMonth: 1, // Febrero
            },
            {
                cards: [
                    {
                        label: _('Siembra Temprana'),
                        title: _('Incentivá a los productores a registrar los datos de cosecha'),
                        description: _(
                            'Al cargar la información, podrán acceder a un reporte detallado de la campaña con resultados clave para planificar la próxima.'
                        ),
                    },
                    {
                        label: _('Siembra tardía'),
                        title: _('Ayudá a los productores a estimar el rendimiento de su cosecha'),
                        description: _(
                            'A través de la carga de muestras los productores pueden estimar el rendimiento y conocer la fecha probable de cosecha.'
                        ),
                        actionText: _('Ver cómo calcular el rendimiento'),
                        action: () => {
                            setOpenYieldEstimationModal(true);
                        },
                    },
                ],
                fromMonth: 2, // Marzo
                toMonth: 2, // Marzo
            },
            {
                cards: [
                    {
                        label: '',
                        title: _('Ayudá a los productores a planificar su próxima campaña'),
                        description: _('Recomendales el híbrido más adecuado para sus lotes.'),
                        actionText: _('Recomendar híbridos Dekalb'),
                        action: goToSeedRecommendation,
                    },
                ],
                fromMonth: 3, // Abril
                toMonth: 3, // Abril
            },
            {
                cards: [
                    {
                        label: '',
                        title: _('Asegurate de que los productores planifiquen el barbecho con el herbicida adecuado'),
                        description: _('Un control eficiente de malezas es clave para optimizar la siembra.'),
                        actionText: _('Recomendar herbicidas'),
                        action: () => goToCPRecommendation({ qs: { type: 'weeds' } }),
                    },
                ],
                fromMonth: 4, // Mayo
                toMonth: 6, // Julio
            },
            {
                cards: [
                    {
                        label: _('Siembra Temprana'),
                        title: _('Recomendá la densidad de siembra óptima para el híbrido'),
                        description: _(
                            'Ayudá a los productores a ajustar la densidad de siembra para maximizar el rendimiento de sus cultivo.'
                        ),
                        actionText: _('Recomendar densidad de siembra'),
                        action: () => {
                            mpTrackEvent('Corn Density Recommendation began', {
                                entry_source: 'rv-home-suggestions',
                            });
                            navigate(
                                reverse('densityRecommendation.DensityRecommendationCreate', {
                                    qs: { entrySource: 'rv-home-suggestions' },
                                })
                            );
                        },
                    },
                    {
                        label: _('Siembra tardía'),
                        title: _('Asegurate de que los productores planifiquen el barbecho con el herbicida adecuado'),
                        description: _('Un control eficiente de malezas es clave para optimizar la siembra.'),
                        actionText: _('Recomendar herbicidas'),
                        action: () => goToCPRecommendation({ qs: { type: 'weeds' } }),
                    },
                ],
                fromMonth: 7, // Agosto
                toMonth: 7, // Agosto
            },
            {
                cards: [
                    {
                        label: _('Siembra Temprana'),
                        title: _('Incentivá a tus productores a Agregar sus cultivos en {{appName}}', {
                            appName: settings.app.name,
                        }),
                        description: _(
                            'Al registrar los cultivos, podrán hacer un seguimiento en cada etapa, acceder a recomendaciones, alertas y estrategias de manejo.'
                        ),
                    },
                    {
                        label: _('Siembra tardía'),
                        title: _('Asegurate de que los productores planifiquen el barbecho con el herbicida adecuado'),
                        description: _('Un control eficiente de malezas es clave para optimizar la siembra.'),
                        actionText: _('Recomendar herbicidas'),
                        action: () => goToCPRecommendation({ qs: { type: 'weeds' } }),
                    },
                ],
                fromMonth: 8, // Septiembre
                toMonth: 8, // Septiembre
            },
            {
                cards: [
                    {
                        label: _('Siembra Temprana'),
                        title: _('Ayuda al productor a controlar las malezas en post emergencia'),
                        description: _('El manejo de malezas es clave para evitar la competencia con el cultivo.'),
                        actionText: _('Recomendar fitosanitarios'),
                        action: () => goToCPRecommendation({ qs: { type: 'weeds' } }),
                    },
                    {
                        label: _('Siembra tardía'),
                        title: _('Asegurate de que los productores planifiquen el barbecho con el herbicida adecuado'),
                        description: _('Un control eficiente de malezas es clave para optimizar la siembra.'),
                        actionText: _('Recomendar herbicidas'),
                        action: () => goToCPRecommendation({ qs: { type: 'weeds' } }),
                    },
                ],
                fromMonth: 9, // Octubre
                toMonth: 9, // Octubre
            },
            {
                cards: [
                    {
                        label: _('Siembra Temprana'),
                        title: _('Recomendale a los productores optimizar la nutrición con N-Smart'),
                        description: _(
                            'Sugerile a los productores el uso de N-Smart para mejorar la disponibilidad de nitrógeno y potenciar el desarrollo del cultivo.'
                        ),
                    },
                    {
                        label: _('Siembra tardía'),
                        title: _('Recomendá la densidad de siembra para el híbrido'),
                        description: _(
                            'Ayudá a los productores a ajustar la densidad de siembra para maximizar el rendimiento de sus cultivo.'
                        ),
                        actionText: _('Recomendar densidad de siembra'),
                        action: () => goToDensityRecommendation(),
                    },
                ],
                fromMonth: 10, // Noviembre
                toMonth: 10, // Noviembre
            },
            {
                cards: [
                    {
                        label: _('Siembra Temprana'),
                        title: _('Seguí de cerca el monitoreo de plagas y enfermedades de tus productores'),
                        description: _(
                            'Evaluá el estado del cultivo y recomendales los fungicidas e insecticidas adecuados.'
                        ),
                        actionText: _('Recomendar fitosanitarios'),
                        action: () => goToCPRecommendation({}),
                    },
                    {
                        label: _('Siembra tardía'),
                        title: _('Incentivá a tus productores a Agregar sus cultivos en {{appName}}', {
                            appName: settings.app.name,
                        }),
                        description: _(
                            'Al registrar los cultivos, podrán hacer un seguimiento en cada etapa, acceder a recomendaciones, alertas y estrategias de manejo.'
                        ),
                    },
                ],
                fromMonth: 11, // Diciembre
                toMonth: 11, // Diciembre
            },
        ],
        [i18n.resolvedLanguage]
    );

    const activeSuggestion = useMemo(() => {
        const currentMonth = new Date().getMonth();
        return suggestions.find(
            (suggestion) => currentMonth >= suggestion.fromMonth && currentMonth <= suggestion.toMonth
        );
    }, [suggestions]);

    return (
        <Box
            sx={{
                border: (t) => `1px solid ${t.palette.divider}`,
                padding: '24px',
                backgroundColor: '#FFFFFF',
                flex: 1,
                width: '100%',
                minHeight: 0,
            }}
        >
            <Stack spacing={3} height='100%'>
                <Stack spacing={1}>
                    <Typography color='primary' variant='h5'>
                        {_('Guía de recomendaciones')}
                    </Typography>
                    <Typography color='primary.mui.inactive' variant='body2'>
                        {_('Sugerencias y acciones que te pueden ayudar a organizar tu trabajo con los productores.')}
                    </Typography>
                </Stack>
                <Stack spacing={2}>
                    {activeSuggestion?.cards?.map((suggestion, index) => (
                        <ScheduleCard
                            // biome-ignore lint/suspicious/noArrayIndexKey: false positive
                            key={index}
                            label={suggestion.label}
                            title={suggestion.title}
                            description={suggestion.description}
                            actionText={suggestion.actionText}
                            actionOnClick={suggestion.action}
                        />
                    ))}
                </Stack>
            </Stack>
        </Box>
    );
}

export default SuggestionsSection;
